import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { FileText, Plug, Layers, ArrowRight, Check } from 'lucide-react';
import LandingNavBar from '../../components/LandingNavBar';
import LandingFooter from '../../components/LandingFooter';

const heading = { fontFamily: "'Plus Jakarta Sans', 'Manrope', sans-serif" };

const options = [
  {
    id: 'einvoice',
    icon: FileText,
    title: 'Use e-Invoicing',
    desc: 'Auto-generate, submit & manage LHDN-compliant e-invoices.',
    recommended: true,
    redirect: '/einvoice/portal/dashboard'
  },
  {
    id: 'pms',
    icon: Plug,
    title: 'Connect my PMS / Channel Manager',
    desc: 'Sync bookings from Beds24, Cloudbeds and more.',
    recommended: false,
    redirect: '/einvoice/portal/dashboard'
  },
  {
    id: 'explore',
    icon: Layers,
    title: 'Explore full platform',
    desc: 'See everything StayMii offers — compliance, calendar, reports.',
    recommended: false,
    redirect: '/einvoice/portal/dashboard'
  }
];

export default function PostSignupOnboarding() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState('einvoice');

  const handleContinue = () => {
    const opt = options.find(o => o.id === selected);
    navigate(opt?.redirect || '/einvoice/portal/dashboard');
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <LandingNavBar />

      <div className="flex-1 flex flex-col lg:flex-row">
        {/* ── LEFT: OPTIONS ── */}
        <div className="flex-1 flex items-center justify-center p-6 lg:p-12 xl:p-24">
          <div className="w-full max-w-lg">
            <div className="mb-10">
              <p className="text-xs font-semibold text-[#2563EB] tracking-[0.15em] uppercase mb-3">Welcome to StayMii</p>
              <h1 className="text-3xl sm:text-4xl font-light tracking-tighter text-slate-900 mb-3" style={heading}>
                What would you like to<br />do with StayMii?
              </h1>
              <p className="text-slate-500 text-base">Choose your starting point. You can always change this later.</p>
            </div>

            <div className="space-y-3 mb-8" data-testid="onboarding-options">
              {options.map(opt => {
                const isSelected = selected === opt.id;
                const Icon = opt.icon;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setSelected(opt.id)}
                    className={`w-full flex items-center gap-4 p-5 rounded-2xl border-2 text-left transition-all duration-200 ${
                      isSelected
                        ? 'border-[#2563EB] bg-blue-50/50'
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                    data-testid={`onboarding-option-${opt.id}`}
                  >
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
                      isSelected ? 'bg-[#2563EB]' : 'bg-slate-100'
                    }`}>
                      <Icon className={`w-5 h-5 ${isSelected ? 'text-white' : 'text-slate-500'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-base font-semibold ${isSelected ? 'text-slate-900' : 'text-slate-700'}`}>
                          {opt.title}
                        </span>
                        {opt.recommended && (
                          <span className="px-2 py-0.5 bg-[#2563EB] text-white text-[10px] font-bold rounded-full uppercase tracking-wider">
                            Recommended
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-500 mt-0.5">{opt.desc}</p>
                    </div>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${
                      isSelected ? 'border-[#2563EB] bg-[#2563EB]' : 'border-slate-300'
                    }`}>
                      {isSelected && <Check className="w-3.5 h-3.5 text-white" />}
                    </div>
                  </button>
                );
              })}
            </div>

            <Button
              onClick={handleContinue}
              className="w-full h-14 rounded-xl bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-base font-semibold transition-all"
              data-testid="onboarding-continue-btn"
            >
              <span className="flex items-center justify-center gap-2">
                Continue <ArrowRight className="w-4 h-4" />
              </span>
            </Button>

            <p className="text-center text-xs text-slate-400 mt-4">You can change this anytime from your dashboard settings.</p>
          </div>
        </div>

        {/* ── RIGHT: TEAM IMAGE ── */}
        <div className="hidden lg:block lg:w-1/2 relative">
          <img
            src="https://customer-assets.emergentagent.com/job_5e74271a-8fbb-4f1f-8b01-d4ef0a762567/artifacts/38boy9y3_IMG_4014.jpeg"
            alt="StayMii team collaborating"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-10">
            <p className="text-white/60 text-xs font-semibold tracking-[0.15em] uppercase mb-3">Our Team</p>
            <h2 className="text-3xl font-light text-white tracking-tight leading-snug" style={heading}>
              Built by Hospitality<br />Professionals
            </h2>
            <p className="text-white/70 text-sm mt-3 max-w-sm leading-relaxed">
              Our team of compliance experts and engineers work together to make e-invoicing effortless for every property in Malaysia.
            </p>
          </div>
        </div>
      </div>

      <LandingFooter />
    </div>
  );
}
